/**
 * Диалог «Поздний выезд» (design-guest §7 действие #2).
 * Гость выбирает время, до которого хочет остаться в день выезда →
 * POST /v1/stays/{id}/late-checkout (подтверждает менеджер; цена/бесплатно
 * проставляется на стороне отеля).
 */
import { useState } from 'react';
import { Button } from '@hsc/ui';
import type { StayResponse } from '@hsc/types';
import { formatDate } from '../../lib/fleet-format.js';
import { useRequestLateCheckout } from './useGuestStay.js';

const TIME_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '14:00', label: 'до 14:00' },
  { value: '16:00', label: 'до 16:00' },
  { value: '18:00', label: 'до 18:00' },
];

export interface LateCheckoutDialogProps {
  stay: StayResponse;
  onClose: () => void;
  onDone: (ok: boolean) => void;
}

export function LateCheckoutDialog({
  stay,
  onClose,
  onDone,
}: LateCheckoutDialogProps): JSX.Element {
  const mut = useRequestLateCheckout();
  const [time, setTime] = useState<string>('16:00');

  const submit = (): void => {
    const day = stay.to.slice(0, 10);
    const lateUntil = new Date(`${day}T${time}:00`).toISOString();
    mut.mutate(
      {
        stayId: stay.id,
        input: {
          late_until: lateUntil,
          ...(stay.guest_user_id ? { guest_id: stay.guest_user_id } : {}),
        },
      },
      { onSuccess: () => onDone(true), onError: () => onDone(false) },
    );
  };

  return (
    <section className="guest-stay-form" data-testid="late-dialog">
      <h1 className="hsc-page__title">Поздний выезд</h1>
      <p className="ta-muted">
        День выезда — {formatDate(stay.to)}. До какого времени хотите остаться?
      </p>
      <div className="fleet-tabs" role="radiogroup" aria-label="Время выезда" data-testid="late-times">
        {TIME_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={time === o.value}
            className={`fleet-tab${time === o.value ? ' is-active' : ''}`}
            onClick={() => setTime(o.value)}
            data-testid={`late-time-${o.value}`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <p className="ta-muted" style={{ marginTop: 8 }}>
        Стоимость зависит от загрузки — менеджер подтвердит при согласовании.
      </p>
      <div className="room-edit__footer">
        <Button onClick={submit} disabled={mut.isPending} data-testid="late-submit">
          {mut.isPending ? 'Отправляем…' : 'Отправить запрос'}
        </Button>
        <Button variant="secondary" onClick={onClose} data-testid="late-back">
          Назад
        </Button>
      </div>
    </section>
  );
}
